import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { useReferrals } from '@/api/queries'
import { SearchIcon, UsersIcon } from '@/components/icons'
import { Pager, td, th } from '@/components/ui/Controls'
import { EmptyState, ErrorState, Skeleton } from '@/components/ui/Feedback'
import { PageHeader } from '@/components/ui/Page'
import { Pill, REFERRAL_STATUS } from '@/components/ui/StatusPill'
import { describeError } from '@/lib/errors'
import { date, humanise, money, pct } from '@/lib/format'
import { cn } from '@/lib/utils'
import type { Referral } from '@/types/api'

const PAGE_SIZE = 25

export function UsersPage() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const query = useReferrals(page, PAGE_SIZE)
  const rows = query.data?.data ?? []
  const info = query.data?.pageInfo

  const term = search.trim().toLowerCase()
  const filtered = useMemo(
    () => (term ? rows.filter((r) => [r.userName, r.userEmailMasked, r.couponCode, r.plan, r.status].some((v) => v?.toLowerCase().includes(term))) : rows),
    [rows, term],
  )

  return (
    <>
      <PageHeader
        title="Users onboarded"
        description="Everyone whose first paid checkout used one of your codes. Emails are masked to protect their privacy."
        actions={
          <label className="relative w-full sm:w-[280px]">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-faint" />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search name, coupon, plan"
              aria-label="Search users"
              className="h-9 w-full rounded-field border border-line bg-surface pl-9 pr-3 text-[14px] text-ink placeholder:text-ink-faint focus:border-brand/60 focus:outline-none focus:ring-2 focus:ring-brand/15"
            />
          </label>
        }
      />

      {query.isError && !query.data ? (
        <div className="card">
          <ErrorState {...describeError(query.error, 'Could not load your users')} onRetry={() => void query.refetch()} />
        </div>
      ) : query.isLoading ? (
        <div className="card space-y-3 p-5">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-11" />
          ))}
        </div>
      ) : (info?.totalItems ?? rows.length) === 0 ? (
        <div className="card">
          <EmptyState
            icon={<UsersIcon className="h-8 w-8" />}
            title="No users onboarded yet"
            description="Share a coupon link. When someone pays for a plan with your code, they show up here with what you earned."
            action={
              <Link to="/coupons" className="inline-flex h-10 items-center justify-center rounded-field bg-ink px-4 text-[14px] font-medium text-oncolor hover:bg-ink/90">
                Go to your coupons
              </Link>
            }
          />
        </div>
      ) : (
        <div className={cn('card overflow-hidden transition-opacity', query.isFetching && 'opacity-70')}>
          {term && (
            <p className="border-b border-line bg-surface-muted/60 px-5 py-2 text-[12.5px] text-ink-muted">
              {filtered.length} of {rows.length} on this page match “{search.trim()}”.
            </p>
          )}
          {filtered.length === 0 ? (
            <EmptyState title="No match on this page" description="Try another name or code, or move to the next page." />
          ) : (
            <>
              <ul className="divide-y divide-line md:hidden">
                {filtered.map((r) => (
                  <UserCard key={r.id} row={r} />
                ))}
              </ul>
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full">
                  <thead className="border-b border-line bg-surface-muted/60">
                    <tr>
                      <th className={th}>User</th>
                      <th className={th}>Coupon</th>
                      <th className={th}>Plan</th>
                      <th className={th}>Onboarded</th>
                      <th className={cn(th, 'text-right')}>Paid</th>
                      <th className={cn(th, 'text-right')}>You earned</th>
                      <th className={th}>Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {filtered.map((r) => (
                      <tr key={r.id} className="hover:bg-surface-muted/50">
                        <td className={td}>
                          <p className="font-medium">{r.userName || 'Unnamed user'}</p>
                          <p className="text-[12.5px] text-ink-muted">{r.userEmailMasked}</p>
                        </td>
                        <td className={td}>
                          <p className="num font-medium">{r.couponCode}</p>
                          <p className="text-[12px] text-ink-muted">
                            {pct(r.discountPct)} off · {pct(r.commissionPct)} to you
                          </p>
                        </td>
                        <td className={cn(td, 'text-ink-soft')}>
                          {humanise(r.plan)} <span className="text-ink-faint">· {humanise(r.billingPeriod)}</span>
                        </td>
                        <td className={cn(td, 'text-ink-soft')}>{date(r.onboardedAt)}</td>
                        <td className={cn(td, 'num text-right')}>{money(r.paidCents)}</td>
                        <td className={cn(td, 'num text-right font-semibold text-brand')}>{money(r.earnedCents)}</td>
                        <td className={td}>
                          <Pill map={REFERRAL_STATUS} value={r.status} size="sm" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {info && <Pager page={info.page} totalPages={info.totalPages} totalItems={info.totalItems} pageSize={info.pageSize} onPage={setPage} />}
        </div>
      )}
    </>
  )
}

function UserCard({ row: r }: { row: Referral }) {
  return (
    <li className="px-4 py-3.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-[14.5px] font-medium text-ink">{r.userName || 'Unnamed user'}</p>
          <p className="truncate text-[12.5px] text-ink-muted">{r.userEmailMasked}</p>
        </div>
        <Pill map={REFERRAL_STATUS} value={r.status} size="sm" />
      </div>
      <div className="mt-2 flex items-end justify-between gap-3 text-[12.5px]">
        <p className="min-w-0 text-ink-muted">
          <span className="num font-medium text-ink-soft">{r.couponCode}</span> · {humanise(r.plan)} {humanise(r.billingPeriod).toLowerCase()} · {date(r.onboardedAt)}
        </p>
        <p className="shrink-0 text-right">
          <span className="num block text-[14px] font-semibold text-brand">{money(r.earnedCents)}</span>
          <span className="num text-ink-faint">paid {money(r.paidCents)}</span>
        </p>
      </div>
    </li>
  )
}
